import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import * as XLSX from "xlsx";
import { createContact, getAssignToList, uploadExcel } from "../types/api";
import { splitFullName } from "../utils/name";
import { stripExtraPhoneChars } from "../utils/phone";
import { BulkResultRow, BulkUploadResult } from "../types/types";
import { Modal, Table, Statistic, Row, Col, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Upload, Button, Space } from "antd";
import { UploadOutlined, EyeOutlined, CloudUploadOutlined, FileDoneOutlined } from "@ant-design/icons";
import { Progress } from "antd";
import axios from "axios";

// ------------------ Types ------------------
const ManualSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  mobile: z.string().min(5, "Mobile required"),
  job: z.string().min(1, "Job is required"),
  city: z.string().optional(),
  assignTo: z.string().optional(),
});
type ManualForm = z.infer<typeof ManualSchema>;

type LocalPreviewRow = {
  rowIndex: number;
  firstName: string;
  middleName?: string;
  lastName?: string;
  mobile: string;
  job: string;
  city?: string;
  assignee?: string;
  error?: string;
};

// ------------------ Component ------------------
export default function ContactUploadForm() {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ManualForm>({ resolver: zodResolver(ManualSchema) });

  // ------------------ States ------------------
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<LocalPreviewRow[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUploadResult | null>(null);
  const [users, setUsers] = useState<{ id: string; userName: string; mobileNumber: string }[]>([]);

  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    onChange: (page: number) => {
      setCurrentPage(page);
    },
    onShowSizeChange: (_: number, size: number) => {
      setPageSize(size);
      setCurrentPage(1); // reset to first page
    },
  };
  // ------------------ Load Assign To List ------------------
  useEffect(() => {
    getAssignToList()
      .then(setUsers)
      .catch(() => message.error("Failed to load users"));
  }, []);

  // ------------------ File Select ------------------
  const onSelectFile = (f: File | null) => {
    setFile(f);
    setPreviewRows([]);
    setBulkResult(null);
  };

  // ------------------ Parse Excel / CSV ------------------
  const parseExcel = async () => {
    if (!file) return message.warning("No file selected");

    try {
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      let wb;
      if (isCsv) {
        const text = await file.text();
        wb = XLSX.read(text, { type: "string", raw: false });
      } else {
        const buf = await file.arrayBuffer();
        wb = XLSX.read(buf, { type: "array", raw: false });
      }

      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!rows.length) return message.warning("File is empty");

      // Map headers
      const header = (rows[0] as string[]).map((h) => String(h || "").toLowerCase().trim());
      const idxName = header.findIndex((h) => h.includes("first_name") || h.includes("full_name"));
      const idxMobile = header.findIndex((h) => h.includes("phone_number") ||  h.includes("phone") || h.includes("mobile"));
      const idxJob = header.findIndex((h) => h.includes("job"));
      const idxCity = header.findIndex((h) => h.includes("city"));
      const idxAssignee = header.findIndex((h) => h.includes("assignee"));
      //console.log("idxName" + idxName);
      const pv: LocalPreviewRow[] = [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i] || [];
        const name = String(row[idxName] || "").trim();
        const mobile = stripExtraPhoneChars(String(row[idxMobile] || ""));
        const job = String(row[idxJob] || "").trim();
        const city = String(row[idxCity] || "").trim();
        const assignee = String(row[idxAssignee] || "").trim();

        if (!name && !mobile ) continue;
        
        console.log("name: " + name + "; mobile: " + mobile);
        const { firstName, middleName, lastName } = splitFullName(name);
        const error = !mobile || !name ? "Missing mobile number or Name" : undefined;

        pv.push({ rowIndex: i + 1, firstName, middleName, lastName, mobile, job, city, assignee, error });
      }

      setPreviewRows(pv);
      setShowPreviewModal(true);
      message.success(`Parsed ${pv.length} rows. Review before uploading.`);
    } catch (err) {
      console.error(err);
      message.error("Failed to parse Excel / CSV file.");
    }
  };

  // ------------------ Upload Excel ------------------
  const uploadFile_old = async () => {
    if (!file) return message.warning("No file selected");
    try {
      setUploading(true);
      setUploadProgress(0);
      const res = await uploadExcel(file);
      setBulkResult(res);
      setShowResultModal(true);
      message.success(`Upload complete: ${res.inserted}/${res.totalRows} inserted`);
    } catch (err: any) {
      console.error(err);
      message.error(err?.response?.data?.message || "Upload failed");
    }
  };

  const uploadFile = async () => {
    if (!file) return message.warning("No file selected");

    try {
      setUploading(true);
      setUploadProgress(0);
      const res = await uploadExcel(file, (percent) => {
        setUploadProgress(percent);
      });

      setBulkResult(res);
      setShowResultModal(true);

      message.success(`Upload complete: ${res.inserted}/${res.totalRows} inserted`);
    } catch (err: any) {
      console.error(err);
      message.error(err?.response?.data?.message || "Upload failed");
    } finally {
      console.log("Uploading file Completed: " + uploading);
      setUploading(false);
      console.log("Uploading file Completed: " + uploading);
    }
  };
  
  // ------------------ Manual Form Submit ------------------
  const submitManual = async (data: ManualForm) => {
    try {
      const body = { ...data, mobile: stripExtraPhoneChars(data.mobile) };
      const res = await createContact(body);
      reset();
      message.success(`Saved contact ${res.firstName} (${res.mobileE164})`);
    } catch (err: any) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to save contact");
    }
  };

  // ------------------ Table Columns ------------------
  const columns: ColumnsType<LocalPreviewRow> = [
    { title: "Row", dataIndex: "rowIndex", key: "rowIndex", width: 80 },
    { title: "First Name", dataIndex: "firstName", key: "firstName" },
    { title: "Middle Name", dataIndex: "middleName", key: "middleName" },
    { title: "Last Name", dataIndex: "lastName", key: "lastName" },
    { title: "Mobile", dataIndex: "mobile", key: "mobile" },
    { title: "Job", dataIndex: "job", key: "job" },
    { title: "Assignee", dataIndex: "assignee", key: "assignee" },
    {
      title: "Status",
      key: "status",
      render: (_, record) =>
        record.error ? <Tag color="red">{record.error}</Tag> : <Tag color="green">OK</Tag>,
    },
  ];

const resultColumns: ColumnsType<BulkResultRow> = [
  {
    title: "Row",
    dataIndex: "rowIndex",
    key: "rowIndex",
    width: 80,
  },
  {
    title: "Name",
    dataIndex: ["contact", "name"],
    key: "name",
    render: (text) => text || "-",
  },
  {
    title: "Mobile",
    dataIndex: ["contact", "mobileE164"],
    key: "mobile",
    render: (text) => text || "-",
  },
  {
    title: "assignTo",
    dataIndex: ["contact", "assignTo"],
    key: "assignTo",
    render: (text) => text || "-",
  },
  {
    title: "Status",
    key: "status",
    render: (_, record) =>
      record.error ? (
          <Tag color="red">{record.error}</Tag>
        ) : (
          <Tag color="green">OK</Tag>
        )
  },
];

  // ------------------ JSX ------------------
  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">New Contact</h1>

      {/* Manual Form */}
      <div className="bg-white rounded-2xl shadow p-6 mb-8">
        <form
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
          onSubmit={handleSubmit(submitManual)}
        >
          {["firstName", "middleName", "lastName", "mobile", "job"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium">{field.replace(/([A-Z])/g, " $1")}</label>
              <input
                className="mt-1 w-full border rounded-lg p-2"
                {...register(field as keyof ManualForm)}
              />
              {errors[field as keyof ManualForm] && (
                <p className="text-red-600 text-sm">
                  {errors[field as keyof ManualForm]?.message as string}
                </p>
              )}
            </div>
          ))}

          {/* Assign To */}
          <div>
            <label className="block text-sm font-medium">Assign To</label>
            <select
              className="mt-1 w-full border rounded-lg p-2"
              {...register("assignTo")}
              onChange={(e) => setValue("assignTo", e.target.value)}
            >
              <option value="">-- Select User --</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.userName} - {u.mobileNumber}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              disabled={isSubmitting}
              className="px-4 py-2 bg-black text-white rounded-xl shadow hover:opacity-90"
            >
              Save Contact
            </button>
          </div>
        </form>
      </div>

      {/* Excel Upload */}
      <div className="bg-white rounded-2xl shadow p-6">
        <Space wrap size="middle" align="center">
          {/* File Upload */}
          <Upload
            accept=".xlsx,.xls,.csv"
            beforeUpload={(file) => {
              onSelectFile(file);
              return false; // prevent auto upload
            }}
            showUploadList={false}
            
          >
            <Button
              icon={<UploadOutlined />}
            >
              Select File
            </Button>
          </Upload>

          {/* Preview */}
          {!bulkResult && (
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={parseExcel}
              disabled={!file}
            >
              Preview
            </Button>
          )}

          {/* Upload */}
          {!bulkResult && (
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={uploadFile}
              disabled={!file || uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          )}
          
          {/* Result */}
          {bulkResult && (
            <Button
              icon={<FileDoneOutlined />}
              onClick={() => setShowResultModal(true)}
            >
              Result
            </Button>
          )}
          
          {/* Selected File Name */}
          {file && (
            <span style={{ maxWidth: 220 }} className="truncate text-sm text-gray-600">
              📄 {file.name}
            </span>
          )}
          
        </Space>

        {uploading && (
          <div style={{ width: 300, marginTop: 10 }}>
            <Progress percent={uploadProgress} status="active" />
          </div>
        )}

      </div>

      {/* Preview Modal */}
      <Modal
        title="Preview"
        open={showPreviewModal}
        onCancel={() => setShowPreviewModal(false)}
        width={900}
        footer={null}
      >
        <Table<LocalPreviewRow>
          columns={columns}
          dataSource={previewRows}
          rowKey={(record) => record.rowIndex}
          pagination={paginationConfig}
          scroll={{ x: "max-content", y: 400 }}
          rowClassName={(record) => (record.error ? "bg-red-50" : "")}
        />
      </Modal>

      {/* Result Modal */}
      {bulkResult && (
        <Modal
          title="Result"
          open={showResultModal}
          onCancel={() => setShowResultModal(false)}
          width={900}
          footer={null}
        >
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Statistic
                title="Inserted"
                value={`${bulkResult.inserted}/${bulkResult.totalRows}`}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Assigned"
                value={`${bulkResult.assigned}/${bulkResult.totalRows}`}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Skipped"
                value={`${bulkResult.skipped}/${bulkResult.totalRows}`}
              />
            </Col>
            <Col span={6}>
              <Statistic title="Failed" value={bulkResult.failed} />
            </Col>
          </Row>

          <Table<BulkResultRow>
            columns={resultColumns}
            dataSource={bulkResult.rows}
            rowKey={(record) => record.rowIndex}
            pagination={paginationConfig}
            scroll={{ x: "max-content", y: 400 }}
            rowClassName={(record) => (record.error ? "bg-red-50" : "")}
          />
        </Modal>
      )}
    </div>
  );
}
